import {
  Box,
  Checkbox,
  Collapse,
  debounce,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  TableContainer,
  TableSortLabel,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { visuallyHidden } from '@mui/utils'
import React, { useCallback, useEffect, useState } from 'react'
import { InView } from 'react-intersection-observer'
import FloatingActionButton from './FloatingActionButton'
import ExpandButton from './ExpandButton'
import SearchBar from './SearchBar'
import ErrorPage from '../errors/ErrorPage'
import DownloadIcon from '@mui/icons-material/Download'
import ExportFileDialog from './ExportFileDialog'

function EnhancedTableHead({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  onRequestSort,
  headCells = [],
  expand,
}) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow>
        {expand && <TableCell padding='normal'></TableCell>}
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={headCell.sortable && orderBy === headCell.id ? order : false}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const InfiniteList = ({
  component,
  useInfiniteQuery,
  headCells,
  orderByKey = 'createdAt',
  defaultOrder = ['createdAt', 'updatedAt'].includes(orderByKey) ? 'desc' : 'asc',
  expand = false,
  queryParams = {},
  queryConfig = {},
  onNew,
  newIcon,
  newLabel = 'New',
  idKey = '_id',
  showExportButton = false,
  ...rest
}) => {
  const ChildComponent = component

  const [order, setOrder] = useState(defaultOrder)
  const [orderBy, setOrderBy] = useState(orderByKey)
  const [selected, setSelected] = useState([])
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState(null)
  const [open, setOpen] = useState(false)

  const numCols = headCells.filter(Boolean).length + (expand ? 1 : 0)

  const query = useInfiniteQuery(
    {
      q: search,
      order,
      sort: orderBy,
      ...queryParams,
    },
    queryConfig
  )

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const skeletonLength = 10

  const noItems =
    query.data?.pages.length === 1 &&
    query.data?.pages[0].docs?.length === 0 &&
    !query.hasNextPage &&
    !query.hasPreviousPage &&
    !query.isFetchingNextPage &&
    !query.isFetchingPreviousPage

  return (
    <>
      <Box display='flex' flexDirection='row' columnGap={2}>
        <Box flexGrow={1}>
          <SearchBar value={search} onChange={(value) => setSearch(value)} />
          {showExportButton && (
            <Box display='flex' justifyContent='flex-end' marginTop={2}>
              <Button startIcon={<DownloadIcon />} onClick={() => setOpen(true)}>
                Export Data
              </Button>
            </Box>
          )}
        </Box>
        <ExportFileDialog open={open} setOpen={setOpen} />
        {onNew && (
          <Button
            sx={{ display: { xs: 'none', md: 'flex' } }}
            color='primary'
            startIcon={newIcon}
            variant='contained'
            onClick={onNew}
          >
            {newLabel}
          </Button>
        )}
      </Box>

      <Box sx={{ pb: onNew ? 8 : 0 }}>
        {!query.isFetchingPreviousPage && query.hasPreviousPage && (
          <InView
            threshold={0.3}
            onChange={(inView) => {
              if (inView) {
                query.fetchPreviousPage()
              }
            }}
          ></InView>
        )}

        <Collapse unmountOnExit in={query.isFetchingPreviousPage} timeout={100}>
          <Skeleton width='100%' />
        </Collapse>
        {Boolean(component) && (
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {query.status === 'loading' ? (
              <Box
                maxHeight={{ xs: 'calc(100vh - 64px)', sm: 'calc(100vh - 72px)' }}
                mb={-2}
                overflow='hidden'
                textAlign='center'
              >
                <CircularProgress sx={{ my: 4 }} />
              </Box>
            ) : query.status === 'error' ? (
              <ErrorPage error={query.error} retry={query.refetch} />
            ) : noItems ? (
              <ErrorPage empty error={{ statusText: 'No items' }} />
            ) : (
              query.data?.pages.map((page, index) => (
                <React.Fragment key={page.nextPage}>
                  {page.docs?.map((data, index) => (
                    <Paper sx={{ p: 2, my: 2 }} key={data?._id ?? index}>
                      <ChildComponent data={data} {...rest} />
                    </Paper>
                  ))}
                </React.Fragment>
              ))
            )}
          </Box>
        )}
        <Paper sx={{ display: component ? { xs: 'none', md: 'block' } : 'block', my: 2 }}>
          <TableContainer>
            <Table aria-labelledby='tableTitle' size='small'>
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                headCells={headCells.filter(Boolean)}
                expand={expand}
              />
              <TableBody>
                {query.status === 'loading' ? (
                  <TableRow
                    style={{
                      height: 53,
                    }}
                  >
                    <TableCell colSpan={numCols} sx={{ textAlign: 'center' }}>
                      <CircularProgress sx={{ my: 4 }} />
                    </TableCell>
                  </TableRow>
                ) : query.status === 'error' ? (
                  <TableRow>
                    <TableCell colSpan={numCols}>
                      <ErrorPage error={query.error?.response} retry={query.refetch} />
                    </TableCell>
                  </TableRow>
                ) : noItems ? (
                  <TableRow>
                    <TableCell colSpan={numCols}>
                      <ErrorPage empty error={{ statusText: 'No items' }} />{' '}
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data?.pages.map((page) => (
                    <React.Fragment key={page.nextPage}>
                      {page.docs?.map((row, index) => {
                        return (
                          <React.Fragment key={row[idKey] ?? index}>
                            <TableRow hover tabIndex={0}>
                              {expand && (
                                <TableCell padding='normal'>
                                  <ExpandButton
                                    onClick={() => {
                                      setExpanded(
                                        expanded === row[idKey] ?? index
                                          ? null
                                          : row[idKey] ?? index
                                      )
                                    }}
                                    expanded={expanded === row[idKey] ?? index}
                                  />
                                </TableCell>
                              )}

                              {headCells.filter(Boolean).map((cell) => (
                                <TableCell
                                  key={cell.id}
                                  align={cell.numeric ? 'right' : 'left'}
                                  padding={cell.disablePadding ? 'none' : 'normal'}
                                >
                                  {cell.view ? cell.view(row) : row[cell.id]}
                                </TableCell>
                              ))}
                            </TableRow>
                            {expand && (
                              <TableRow>
                                <TableCell padding='none' colSpan={numCols}>
                                  <Collapse unmountOnExit in={expanded === row[idKey] ?? index}>
                                    <ChildComponent
                                      data={row}
                                      refetch={query?.refetch}
                                      tableView
                                      {...rest}
                                    />
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        )
                      })}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        {!query.isFetchingNextPage && query.hasNextPage && (
          <InView
            threshold={0.3}
            onChange={(inView) => {
              if (inView) {
                query.fetchNextPage()
              }
            }}
          ></InView>
        )}

        <Collapse unmountOnExit in={query.isFetchingNextPage} timeout={100}>
          <Skeleton width='100%' />
        </Collapse>
        {onNew && (
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <FloatingActionButton
              color='primary'
              icon={newIcon}
              label={newLabel}
              in
              variant='extended'
              onClick={onNew}
            />
          </Box>
        )}
      </Box>
    </>
  )
}

export default InfiniteList
